import { mobileApiClient } from '@/utils/api'

const API_BASE_URL = process.env.VUE_APP_MOBILE_API_URL || 'http://localhost:5001/api'
const MOBILE_API_BASE_URL = process.env.VUE_APP_MOBILE_API_URL || 'http://localhost:5001/api'

export default {
  namespaced: true,
  
  state: {
    categories: [],
    kpis: [],
    targets: [],
    measurements: [],
    evaluations: [],
    plans: [],
    planGroups: [],
    incentives: [],
    dashboards: [],
    summary: null,
    selectedAnalytics: null,
    loading: false,
    error: null,
    measurementTypes: [
      'Percentage',
      'Number',
      'Currency',
      'Time',
      'Count',
      'Ratio',
      'Score'
    ],
    calculationMethods: [
      'Sum',
      'Average',
      'Count',
      'Min',
      'Max',
      'Custom Formula'
    ],
    frequencies: [
      'Daily',
      'Weekly',
      'Monthly',
      'Quarterly',
      'Yearly'
    ],
    targetPeriods: [
      'Daily',
      'Weekly',
      'Monthly',
      'Quarterly',
      'Yearly'
    ]
  },
  
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_KPIS(state, kpis) {
      state.kpis = kpis
    },
    SET_TARGETS(state, targets) {
      state.targets = targets
    },
    SET_MEASUREMENTS(state, measurements) {
      state.measurements = measurements
    },
    SET_EVALUATIONS(state, evaluations) {
      state.evaluations = evaluations
    },
    SET_PLANS(state, plans) {
      state.plans = plans
    },
    SET_PLAN_GROUPS(state, planGroups) {
      state.planGroups = planGroups
    },
    SET_INCENTIVES(state, incentives) {
      state.incentives = incentives
    },
    SET_DASHBOARDS(state, dashboards) {
      state.dashboards = dashboards
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary
    },
    SET_SELECTED_ANALYTICS(state, analytics) {
      state.selectedAnalytics = analytics
    },
    ADD_CATEGORY(state, category) {
      state.categories.push(category)
    },
    UPDATE_CATEGORY(state, updatedCategory) {
      const index = state.categories.findIndex(c => c.id === updatedCategory.id)
      if (index !== -1) {
        state.categories.splice(index, 1, updatedCategory)
      }
    },
    REMOVE_CATEGORY(state, categoryId) {
      state.categories = state.categories.filter(c => c.id !== categoryId)
    },
    ADD_KPI(state, kpi) {
      state.kpis.push(kpi)
    },
    UPDATE_KPI(state, updatedKpi) {
      const index = state.kpis.findIndex(k => k.id === updatedKpi.id)
      if (index !== -1) {
        state.kpis.splice(index, 1, updatedKpi)
      }
    },
    REMOVE_KPI(state, kpiId) {
      state.kpis = state.kpis.filter(k => k.id !== kpiId)
    },
    ADD_TARGET(state, target) {
      state.targets.push(target)
    },
    UPDATE_TARGET(state, updatedTarget) {
      const index = state.targets.findIndex(t => t.id === updatedTarget.id)
      if (index !== -1) {
        state.targets.splice(index, 1, updatedTarget)
      }
    },
    REMOVE_TARGET(state, targetId) {
      state.targets = state.targets.filter(t => t.id !== targetId)
    },
    ADD_MEASUREMENT(state, measurement) {
      state.measurements.push(measurement)
    },
    UPDATE_MEASUREMENT(state, updatedMeasurement) {
      const index = state.measurements.findIndex(m => m.id === updatedMeasurement.id)
      if (index !== -1) {
        state.measurements.splice(index, 1, updatedMeasurement)
      }
    },
    REMOVE_MEASUREMENT(state, measurementId) {
      state.measurements = state.measurements.filter(m => m.id !== measurementId)
    },
    ADD_EVALUATION(state, evaluation) {
      state.evaluations.push(evaluation)
    },
    UPDATE_EVALUATION(state, updatedEvaluation) {
      const index = state.evaluations.findIndex(e => e.id === updatedEvaluation.id)
      if (index !== -1) {
        state.evaluations.splice(index, 1, updatedEvaluation)
      }
    },
    REMOVE_EVALUATION(state, evaluationId) {
      state.evaluations = state.evaluations.filter(e => e.id !== evaluationId)
    }
  },
  
  actions: {
    // KPI Categories
    async fetchCategories({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        console.log('🔍 Fetching KPI categories with params:', params)
        console.log('🔗 API URL:', `${process.env.VUE_APP_MOBILE_API_URL || 'http://localhost:5001/api'}/Kpi/categories`)
        
        const response = await mobileApiClient.get(`/Kpi/categories`, { params })
        console.log('📡 KPI Categories API Response:', response)
        console.log('📡 Response type:', typeof response)
        console.log('📡 Response structure:', response)
        
        if (!response) {
          console.error('❌ Response is null or undefined')
          commit('SET_ERROR', 'No response received from API')
          commit('SET_CATEGORIES', [])
          return
        }
        
        // Handle the actual API response structure
        const categories = response.model || response.data?.model || response.data || []
        if (!categories || !Array.isArray(categories)) {
          console.error('❌ Categories data is not an array:', categories)
          commit('SET_ERROR', 'Invalid categories data format')
          commit('SET_CATEGORIES', [])
          return
        }
        
        console.log('✅ Setting KPI categories:', categories)
        commit('SET_CATEGORIES', categories)
      } catch (error) {
        console.error('❌ KPI categories fetch error:', error)
        console.error('❌ Error type:', typeof error)
        console.error('❌ Error message:', error.message)
        console.error('❌ Error response:', error.response)
        console.error('❌ Error response data:', error.response?.data)
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch KPI categories')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createCategory({ commit }, categoryData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/categories`, categoryData)
        if (response.data.didError === false) {
          commit('ADD_CATEGORY', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to create category')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to create category')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateCategory({ commit }, categoryData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.put(`/Kpi/categories`, categoryData)
        if (response.data.didError === false) {
          commit('UPDATE_CATEGORY', categoryData)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to update category')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to update category')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteCategory({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.delete(`/Kpi/categories/${id}`)
        if (response.data.didError === false) {
          commit('REMOVE_CATEGORY', id)
        } else {
          throw new Error(response.data.errorMessage || 'Failed to delete category')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to delete category')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // KPIs
    async fetchKpis({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        console.log('🔍 Fetching KPIs with params:', params)
        const response = await mobileApiClient.get(`/Kpi/kpis`, { params })
        console.log('📡 KPI API Response:', response)
        
        if (!response) {
          console.error('❌ Invalid response structure:', response)
          commit('SET_ERROR', 'Invalid API response structure')
          commit('SET_KPIS', [])
          return
        }
        
        // Handle the actual API response structure
        const kpis = response.model || response.data?.model || response.data || []
        if (!kpis || !Array.isArray(kpis)) {
          console.error('❌ KPIs data is not an array:', kpis)
          commit('SET_ERROR', 'Invalid KPIs data format')
          commit('SET_KPIS', [])
          return
        }
        
        console.log('✅ Setting KPIs:', kpis)
        commit('SET_KPIS', kpis)
      } catch (error) {
        console.error('❌ KPI fetch error:', error)
        console.error('❌ Error response:', error.response)
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch KPIs')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchKpisByCategory({ commit }, categoryId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/categories/${categoryId}/kpis`)
        const kpis = response.model || response.data?.model || response.data || []
        commit('SET_KPIS', kpis)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch KPIs by category')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchKpiById({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/kpis/${id}`)
        return response.model || response.data?.model || response.data
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createKpi({ commit }, kpiData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/kpis`, kpiData)
        if (response.data.didError === false) {
          commit('ADD_KPI', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to create KPI')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to create KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateKpi({ commit }, kpiData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.put(`/Kpi/kpis`, kpiData)
        if (response.data.didError === false) {
          commit('UPDATE_KPI', kpiData)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to update KPI')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to update KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteKpi({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.delete(`/Kpi/kpis/${id}`)
        if (response.data.didError === false) {
          commit('REMOVE_KPI', id)
        } else {
          throw new Error(response.data.errorMessage || 'Failed to delete KPI')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to delete KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // KPI Targets
    async fetchTargets({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/targets`, { params })
        commit('SET_TARGETS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch targets')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchTargetsByKpi({ commit }, kpiId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/targets/kpi/${kpiId}`)
        commit('SET_TARGETS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch targets by KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createTarget({ commit }, targetData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/targets`, targetData)
        if (response.data.didError === false) {
          commit('ADD_TARGET', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to create target')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to create target')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateTarget({ commit }, targetData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.put(`/Kpi/targets`, targetData)
        if (response.data.didError === false) {
          commit('UPDATE_TARGET', targetData)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to update target')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to update target')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteTarget({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.delete(`/Kpi/targets/${id}`)
        if (response.data.didError === false) {
          commit('REMOVE_TARGET', id)
        } else {
          throw new Error(response.data.errorMessage || 'Failed to delete target')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to delete target')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // KPI Measurements
    async fetchMeasurements({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/measurements`, { params })
        commit('SET_MEASUREMENTS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch measurements')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchMeasurementsByKpi({ commit }, kpiId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/measurements/kpi/${kpiId}`)
        commit('SET_MEASUREMENTS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch measurements by KPI')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchMeasurementsByEntity({ commit }, { entityType, entityId }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/measurements/entity/${entityType}/${entityId}`)
        commit('SET_MEASUREMENTS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch measurements by entity')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createMeasurement({ commit }, measurementData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/measurements`, measurementData)
        if (response.data.didError === false) {
          commit('ADD_MEASUREMENT', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to create measurement')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to create measurement')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateMeasurement({ commit }, measurementData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.put(`/Kpi/measurements`, measurementData)
        if (response.data.didError === false) {
          commit('UPDATE_MEASUREMENT', measurementData)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to update measurement')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to update measurement')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteMeasurement({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.delete(`/Kpi/measurements/${id}`)
        if (response.data.didError === false) {
          commit('REMOVE_MEASUREMENT', id)
        } else {
          throw new Error(response.data.errorMessage || 'Failed to delete measurement')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to delete measurement')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // KPI Evaluations
    async fetchEvaluations({ commit }, params = {}) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/evaluations`, { params })
        commit('SET_EVALUATIONS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch evaluations')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchEvaluationsByEmployee({ commit }, empId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/evaluations/employee/${empId}`)
        commit('SET_EVALUATIONS', response.data.model || [])
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch evaluations by employee')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async createEvaluation({ commit }, evaluationData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/evaluations`, evaluationData)
        if (response.data.didError === false) {
          commit('ADD_EVALUATION', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to create evaluation')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to create evaluation')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async updateEvaluation({ commit }, evaluationData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.put(`/Kpi/evaluations`, evaluationData)
        if (response.data.didError === false) {
          commit('UPDATE_EVALUATION', evaluationData)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to update evaluation')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to update evaluation')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async deleteEvaluation({ commit }, id) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.delete(`/Kpi/evaluations/${id}`)
        if (response.data.didError === false) {
          commit('REMOVE_EVALUATION', id)
        } else {
          throw new Error(response.data.errorMessage || 'Failed to delete evaluation')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to delete evaluation')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Mobile API specific actions
    async fetchMobileKpis({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/list`)
        const kpis = response.model || response.data?.model || response.data || []
        commit('SET_KPIS', kpis)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch KPIs')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchMobileKpiCategories({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        console.log('🔍 Fetching mobile KPI categories')
        const response = await mobileApiClient.get(`/Kpi/categories`)
        console.log('📡 Mobile KPI Categories API Response:', response)
        
        if (!response) {
          console.error('❌ Invalid mobile categories response structure:', response)
          commit('SET_ERROR', 'Invalid API response structure')
          commit('SET_CATEGORIES', [])
          return
        }
        
        // Handle the actual API response structure
        const categories = response.model || response.data?.model || response.data || []
        if (!categories || !Array.isArray(categories)) {
          console.error('❌ Mobile categories data is not an array:', categories)
          commit('SET_ERROR', 'Invalid categories data format')
          commit('SET_CATEGORIES', [])
          return
        }
        
        console.log('✅ Setting mobile KPI categories:', categories)
        commit('SET_CATEGORIES', categories)
      } catch (error) {
        console.error('❌ Mobile KPI categories fetch error:', error)
        console.error('❌ Error response:', error.response)
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch categories')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchMobileKpiMeasurementsByEmployee({ commit }, empId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/measurements/employee/${empId}`)
        const measurements = response.model || response.data?.model || response.data || []
        commit('SET_MEASUREMENTS', measurements)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch employee measurements')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchMobileKpiEvaluationsByEmployee({ commit }, empId) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.get(`/Kpi/evaluations/employee/${empId}`)
        const evaluations = response.model || response.data?.model || response.data || []
        commit('SET_EVALUATIONS', evaluations)
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || 'Failed to fetch employee evaluations')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async submitMobileKpiMeasurement({ commit }, measurementData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/measurements/add`, measurementData)
        if (response.data.didError === false) {
          commit('ADD_MEASUREMENT', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to submit measurement')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to submit measurement')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async submitMobileKpiEvaluation({ commit }, evaluationData) {
      try {
        commit('SET_LOADING', true)
        commit('SET_ERROR', null)
        
        const response = await mobileApiClient.post(`/Kpi/evaluations/add`, evaluationData)
        if (response.data.didError === false) {
          commit('ADD_EVALUATION', response.data)
          return response.data
        } else {
          throw new Error(response.data.errorMessage || 'Failed to submit evaluation')
        }
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.errorMessage || error.message || 'Failed to submit evaluation')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Utility actions
    clearError({ commit }) {
      commit('SET_ERROR', null)
    },
    
    clearSelectedAnalytics({ commit }) {
      commit('SET_SELECTED_ANALYTICS', null)
    }
  },
  
  getters: {
    isLoading: state => state.loading,
    error: state => state.error,
    categories: state => state.categories,
    kpis: state => state.kpis,
    targets: state => state.targets,
    measurements: state => state.measurements,
    evaluations: state => state.evaluations,
    plans: state => state.plans,
    planGroups: state => state.planGroups,
    incentives: state => state.incentives,
    dashboards: state => state.dashboards,
    summary: state => state.summary,
    selectedAnalytics: state => state.selectedAnalytics,
    measurementTypes: state => state.measurementTypes,
    calculationMethods: state => state.calculationMethods,
    frequencies: state => state.frequencies,
    targetPeriods: state => state.targetPeriods,
    
    // Computed getters
    activeKpis: state => state.kpis.filter(k => k.isActive),
    activeCategories: state => state.categories.filter(c => c.isActive),
    kpisByCategory: state => categoryId => state.kpis.filter(k => k.kpiCategoryId === categoryId),
    targetsByKpi: state => kpiId => state.targets.filter(t => t.kpiId === kpiId),
    measurementsByKpi: state => kpiId => state.measurements.filter(m => m.kpiId === kpiId),
    evaluationsByEmployee: state => empId => state.evaluations.filter(e => e.empId === empId)
  }
} 